import React, { useContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { FormProvider, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { Box, Divider, Stack, Typography } from '@mui/material_modern'
import { Edit } from '@mui/icons-material'
import { useGetAssetByCategoryAndIdQuery } from 'services/physicalAssets'
import { showFormModal } from 'state/ui/index'
import store from 'state/store'
import ContentLayoutContext from 'components/layouts/ContentLayoutContextProvider'
import TabsLayoutContext from 'components/layouts/TabsLayoutContextProvider'
import SubFormLayout from 'components/layouts/SubFormLayout.jsx'
import { formatAssetForGrid } from 'components/containers/load-board/utils/formatFunctions.ts'
import FORM_MODALS from 'utils/ui/modals'
import useGlobalLoader from 'utils/custom-hooks/useGlobalLoader.js'
import { ASSET_CATEGORY } from 'utils/constants'
import {
  getContainerFields,
  getTrailerFields,
  getTruckFields,
} from 'components/containers/load-board/utils/helperFunctions.jsx'

const AssetDetailContainer = ({ assetCategory }) => {
  const { assetId } = useParams()
  const contentContext = useContext(ContentLayoutContext)
  const tabsContext = useContext(TabsLayoutContext)
  const [assetCategoryTitle, setAssetCategoryTitle] = useState('')
  const [assetDetailFields, setAssetDetailFields] = useState([])
  const methods = useForm()
  const { reset } = methods

  const { data: assetData, isLoading: isLoadingAssetData } = useGetAssetByCategoryAndIdQuery(
    { assetCategory, assetId },
    { refetchOnMountOrArgChange: true },
  )
  useGlobalLoader(isLoadingAssetData)

  useEffect(() => {
    if (assetData) {
      reset(formatAssetForGrid(assetData))
    }
  }, [assetData])

  useEffect(() => {
    tabsContext.setContainerTabsVisibility(true)

    const pageActions = [
      {
        icon: <Edit />,
        text: 'Edit',
        id: `edit-assetData-${ASSET_CATEGORY.CONTAINER}`,
        onClick: () => {
          store.dispatch(
            showFormModal({
              formModalName: FORM_MODALS.CONTAINER_ASSET_BLADE,
              formModalProps: { assetId: assetData?.id },
            }),
          )
        },
      },
      {
        icon: <Edit />,
        text: 'Edit',
        id: `edit-assetData-${ASSET_CATEGORY.TRUCK}`,
        onClick: () => {
          store.dispatch(
            showFormModal({
              formModalName: FORM_MODALS.TRUCK_ASSET_BLADE,
              formModalProps: { assetId: assetData?.id },
            }),
          )
        },
      },
      {
        icon: <Edit />,
        text: 'Edit',
        id: `edit-assetData-${ASSET_CATEGORY.TRAILER}`,
        onClick: () => {
          store.dispatch(
            showFormModal({
              formModalName: FORM_MODALS.TRAILER_ASSET_BLADE,
              formModalProps: { assetId: assetData?.id },
            }),
          )
        },
      },
    ]

    if (assetCategory === ASSET_CATEGORY.CONTAINER) {
      setAssetDetailFields(getContainerFields())
      setAssetCategoryTitle('Container')
    } else if (assetCategory === ASSET_CATEGORY.TRUCK) {
      setAssetDetailFields(getTruckFields())
      setAssetCategoryTitle('Truck')
    } else if (assetCategory === ASSET_CATEGORY.TRAILER) {
      setAssetDetailFields(getTrailerFields())
      setAssetCategoryTitle('Trailer')
    }

    contentContext.setContainerTitle(
      <Box display="flex" alignItems="center">
        <Typography variant="h5" sx={{ flex: 1, pr: 2 }}>
          {assetData?.assetName || '---'}
        </Typography>
      </Box>,
    )

    contentContext.setContainerActions(pageActions.filter(action => action.id === `edit-assetData-${assetCategory}`))
  }, [assetData])

  return (
    <>
      <FormProvider {...methods}>
        <Stack>
          <Stack direction="column" py={3} px={2} spacing={3} divider={<Divider orientation="vertical" flexItem />}>
            <SubFormLayout
              formName={`${assetCategoryTitle} Properties`}
              boxStyles={{ width: '100%' }}
              fields={assetDetailFields}
            />
          </Stack>
        </Stack>
      </FormProvider>
    </>
  )
}

AssetDetailContainer.propTypes = {
  assetCategory: PropTypes.oneOf([ASSET_CATEGORY.TRUCK, ASSET_CATEGORY.TRAILER, ASSET_CATEGORY.CONTAINER]).isRequired,
}

export default AssetDetailContainer
